import { Link } from "react-router-dom";
import { corporatePackages, images, companyDetails } from "../data";
import { getOptimizedImageUrl } from "../lib/utils";
import SEO from "../components/SEO";

export default function Companies() {
  const companiesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Lederudvikling for Virksomheder",
        "description": "Styrk ledergruppen med et fælles sprog, færre konflikter og klarere lederskab gennem skræddersyede virksomhedspakker og lukkede hold.",
        "provider": {
          "@type": "Organization",
          "name": "Lederskab.nu",
          "url": "https://lederskab.nu"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Virksomheder",
            "item": "https://lederskab.nu/virksomheder"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Lederudvikling for Virksomheder | Skræddersyede Forløb | Lederskab.nu"
        description="Styrk ledergruppen og skab et fælles ledelsessprog. Fleksible virksomhedspakker, lukkede interne forløb og mængderabatter for organisationer."
        canonicalPath="/virksomheder"
        structuredData={companiesSchema}
      />
      {/* Header Banner */}
      <section className="relative bg-gradient-to-br from-maersk-dark to-navy-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.15),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-left">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-orange bg-brand-orange/20 border border-brand-orange/30 px-3 py-1.5 rounded-full">
              For Virksomheder
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 mb-6 leading-tight">
              {corporatePackages.title}
            </h1>
            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed">
              {corporatePackages.description}
            </p>
          </div>
        </div>
      </section>

      {/* Synergies & Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 bg-maersk-light px-3 py-1 rounded-full">
                <i className="fa-solid fa-users-viewfinder text-maersk-blue text-xs" />
                <span className="text-xs font-bold uppercase tracking-wider text-maersk-blue">
                  Styrk organisationen
                </span>
              </div>
              
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark">
                Hvorfor sende flere ledere afsted sammen?
              </h2>
              
              <p className="text-navy-700 leading-relaxed text-base sm:text-lg">
                Når I udvikler jeres ledere i flok, opnår I en langt højere effekt af investeringen. I stedet for at én leder vender hjem med isolerede værktøjer, skaber I en samlet bevægelse i virksomheden.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange-light flex items-center justify-center text-brand-orange shrink-0">
                    <i className="fa-solid fa-comments text-lg" />
                  </div>
                  <div>
                    <h4 className="font-bold text-maersk-dark text-base">Fælles sprog</h4>
                    <p className="text-sm text-navy-700 mt-1">I får et fælles begrebsapparat omkring ledelse, ansvar og trivsel.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange-light flex items-center justify-center text-brand-orange shrink-0">
                    <i className="fa-solid fa-compass text-lg" />
                  </div>
                  <div>
                    <h4 className="font-bold text-maersk-dark text-base">Fælles ledelsesforståelse</h4>
                    <p className="text-sm text-navy-700 mt-1">Ledergruppen trækker i samme retning og leder ud fra de samme sunde principper.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange-light flex items-center justify-center text-brand-orange shrink-0">
                    <i className="fa-solid fa-seedling text-lg" />
                  </div>
                  <div>
                    <h4 className="font-bold text-maersk-dark text-base">Fælles kultur</h4>
                    <p className="text-sm text-navy-700 mt-1">I opbygger en stærk, bæredygtig kultur med fokus på psykologisk tryghed.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange-light flex items-center justify-center text-brand-orange shrink-0">
                    <i className="fa-solid fa-people-group text-lg" />
                  </div>
                  <div>
                    <h4 className="font-bold text-maersk-dark text-base">Stærkere samarbejde</h4>
                    <p className="text-sm text-navy-700 mt-1">Siloer nedbrydes, og lederne sparrer tættere og mere fortroligt i hverdagen.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-maersk-blue/10 rounded-2xl blur-xl" />
                <img
                  src={getOptimizedImageUrl(images.groupDiscussion, "width=600,quality=85,format=auto")}
                  alt="Anita Lauridsen faciliterer lederudvikling for virksomheder"
                  width="600"
                  height="450"
                  loading="lazy"
                  decoding="async"
                  className="relative rounded-2xl shadow-xl border border-maersk-light object-cover w-full h-[450px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Packages Pricing */}
      <section className="py-20 bg-navy-50 border-y border-maersk-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange bg-brand-orange-light px-3 py-1 rounded-full">
              Prisstruktur & Pakker
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark mt-3">
              Virksomhedspakker og priser
            </h2>
            <p className="text-navy-700 mt-3">
              Vi tilbyder gennemskuelige mængderabatter, når I sender flere ledere afsted på det komplette 12-måneders lederforløb.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {corporatePackages.packages.map((pkg, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-maersk-blue/10 shadow-md hover:shadow-xl transition-all duration-300 p-6 flex flex-col justify-between text-left"
              >
                <div>
                  <div className="mb-4">
                    <span className="text-xs font-bold text-brand-orange bg-brand-orange-light px-2.5 py-1 rounded-full border border-brand-orange/10">
                      Holdstørrelse
                    </span>
                    <h3 className="font-display text-xl font-bold text-maersk-dark mt-2">
                      {pkg.range}
                    </h3>
                  </div>

                  <div className="my-6">
                    <span className="text-3xl font-extrabold text-maersk-blue">{pkg.price}</span>
                    <p className="text-xs text-gray-400 mt-1">{pkg.sub}</p>
                  </div>

                  <ul className="space-y-3 border-t border-navy-50 pt-6 text-sm text-navy-700">
                    {pkg.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-brand-orange mt-1 text-xs shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 pt-4 border-t border-navy-50">
                  <Link
                    to="/kontakt"
                    className="w-full block bg-maersk-blue hover:bg-maersk-hover text-white text-center py-3 rounded-md font-bold text-sm tracking-wide transition-all duration-300"
                  >
                    Forespørg her
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Bonus Highlight Box */}
          <div className="bg-brand-orange-light border border-brand-orange/20 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto text-left flex flex-col sm:flex-row items-center gap-6">
            <div className="w-14 h-14 rounded-full bg-brand-orange text-white flex items-center justify-center shrink-0 text-2xl shadow-md">
              <i className="fa-solid fa-gift" />
            </div>
            <div>
              <h4 className="font-display font-bold text-maersk-dark text-lg mb-1">
                Eksklusiv Bonus ved 5+ deltagere
              </h4>
              <p className="text-sm text-navy-700 leading-relaxed">
                {corporatePackages.bonus} Disse sessioner sikrer, at teorien og værktøjerne bliver direkte overført til jeres strategiske hverdag og løser jeres konkrete udfordringer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-maersk-dark max-w-2xl mx-auto">
            Skal vi skræddersy et forløb til jeres virksomhed?
          </h2>
          
          <p className="text-navy-700 text-lg max-w-2xl mx-auto">
            Uanset om I ønsker at sende et par ledere afsted på vores åbne hold, eller I ønsker et fuldt tilpasset internt forløb for hele jeres ledergruppe, står vi klar til at rådgive jer.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            >
              Book en afklarende samtale <i className="fa-solid fa-calendar-check ml-2" />
            </Link>
            <a
              href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`}
              className="bg-maersk-light hover:bg-maersk-blue hover:text-white text-maersk-blue px-8 py-4 rounded-md font-bold tracking-wide transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring på {companyDetails.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
